// Partnership Models — pure calculation engine. All rules come from cfg (admin-editable).

const N = (v) => (v === "" || v === null || v === undefined || isNaN(Number(v)) ? 0 : Number(v));

export const SCENARIOS = ["conservative", "projected", "high"];

const annualFixed = (e, R) => {
  const amt = N(e.fixed_amount);
  if (e.fixed_period === "monthly") return amt * 12;
  if (e.fixed_period === "pct_of_base") return R * amt / 100;
  return amt;
};

export function expenseAmount(e, rev, R) {
  if (e.active === false) return 0;
  if (e.type === "fixed") return annualFixed(e, R);
  if (e.type === "hybrid") return N(e.hybrid_fixed) + rev * N(e.hybrid_pct) / 100;
  return rev * N(e.pct) / 100; // variable
}

// Aggregate variable-fraction and fixed part of opex (for break-even math)
function opexParts(expenses, R) {
  let varPct = 0, fixedAmt = 0;
  for (const e of expenses) {
    if (e.active === false) continue;
    if (e.type === "fixed") fixedAmt += annualFixed(e, R);
    else if (e.type === "hybrid") { fixedAmt += N(e.hybrid_fixed); varPct += N(e.hybrid_pct); }
    else varPct += N(e.pct);
  }
  return { varPct, fixedAmt };
}

export function stageSchedule(cfg, stageId) {
  if (!stageId || cfg.stages?.enabled === false) return null;
  const opt = (cfg.stages?.options || []).find((o) => o.id === stageId);
  const bands = (opt?.schedule || []).filter((b) => N(b.pct) > 0);
  return bands.length ? { opt, bands } : null;
}

export function bandLabel(bands, i) {
  const from = i === 0 ? 1 : N(bands[i - 1].upto_year) + 1;
  const to = N(bands[i].upto_year) || 0;
  if (!to) return `Year ${from}+`;
  return from === to ? `Year ${to}` : `Years ${from}–${to}`;
}

export function computeComparison(R, cfg, stageId = "") {
  const revs = {};
  for (const s of SCENARIOS) revs[s] = R * N(cfg.scenarios[s]?.multiplier ?? (s === "conservative" ? 80 : s === "high" ? 120 : 100)) / 100;

  const expenses = cfg.expenses || [];
  const opex = {}, central = {}, expenseRows = {};
  for (const s of SCENARIOS) {
    expenseRows[s] = expenses.filter((e) => e.active !== false).map((e) => ({ ...e, amount: expenseAmount(e, revs[s], R) }));
    opex[s] = expenseRows[s].reduce((a, e) => a + e.amount, 0);
    central[s] = revs[s] * N(cfg.central_support_pct) / 100;
  }

  const ownerOpex = (model, s) =>
    expenseRows[s].reduce((a, e) => a + ((e.paid_by?.[model] || (model === "management" ? "owner" : "hr")) === "owner" ? e.amount : 0), 0);
  const hrOpex = (model, s) => opex[s] - ownerOpex(model, s);

  const minProfitPct = N(cfg.viability?.min_profit_pct ?? 15);

  // Stage-based offer schedule (year-1 % drives the headline offer for rent and revenue share)
  const ss = stageSchedule(cfg, stageId);
  const y1pct = ss ? N(ss.bands[0].pct) : null;

  // ---- Fixed Rental ----
  const fr = cfg.fixed_rental || {};
  const rentPctEff = y1pct !== null ? y1pct : N(fr.rent_pct);
  const pctRent = R * rentPctEff / 100;
  const manualRent = N(fr.manual_annual) || N(fr.manual_monthly) * 12;
  const targetProfitCons = revs.conservative * minProfitPct / 100;
  const sustainableCeiling = revs.conservative - opex.conservative - central.conservative - targetProfitCons;
  let rent;
  if (fr.method === "manual") rent = manualRent;
  else if (fr.method === "sustainable") rent = Math.min(pctRent, sustainableCeiling);
  else rent = pctRent;
  if (N(fr.override_annual) > 0) rent = N(fr.override_annual);
  rent = Math.max(0, rent);

  // ---- Management ----
  const mg = cfg.management || {};
  const mgmtFee = {};
  for (const s of SCENARIOS) {
    const rev = revs[s];
    let pct = N(mg.fee_pct);
    if (mg.slabs_enabled && (mg.slabs || []).length) {
      const sorted = [...mg.slabs].filter((x) => N(x.upto) > 0 || N(x.pct) > 0).sort((a, b) => N(a.upto) - N(b.upto));
      let matched = null;
      for (const sl of sorted) if (rev <= N(sl.upto)) { matched = sl; break; }
      pct = matched ? N(matched.pct) : (sorted.length ? N(sorted[sorted.length - 1].pct) : pct);
    }
    let fee = rev * pct / 100;
    if (mg.min_fee_enabled) fee = Math.max(fee, Math.max(N(mg.min_annual), N(mg.min_monthly) * 12));
    if (mg.incentive_enabled && rev > N(mg.incentive_threshold)) fee += (rev - N(mg.incentive_threshold)) * N(mg.incentive_pct) / 100;
    if (N(mg.override_fee_annual) > 0) fee = N(mg.override_fee_annual);
    mgmtFee[s] = fee;
  }

  // ---- MG with Revenue Share ----
  const ms = cfg.mg_share || {};
  const mgBaseRev = ms.mg_basis === "projected" ? revs.projected : revs.conservative;
  let minGuarantee = mgBaseRev * N(ms.mg_pct) / 100;
  const manualMG = N(ms.manual_mg_annual) || N(ms.manual_mg_monthly) * 12;
  if (manualMG > 0) minGuarantee = manualMG;
  const mgPayout = {}, mgShareAmt = {};
  const sharePctEff = y1pct !== null ? y1pct : N(ms.share_pct);
  for (const s of SCENARIOS) {
    const rev = revs[s];
    const share = rev * sharePctEff / 100;
    mgShareAmt[s] = share;
    if (ms.payout_method === "manual" && N(ms.manual_payout_annual) > 0) mgPayout[s] = N(ms.manual_payout_annual);
    else if (ms.payout_method === "base_plus_upside") mgPayout[s] = minGuarantee + Math.max(0, rev - N(ms.upside_threshold)) * N(ms.upside_pct) / 100;
    else mgPayout[s] = Math.max(minGuarantee, share);
  }

  // ---- Owner income per model/scenario ----
  const ownerIncome = { fixed: {}, management: {}, mg: {} };
  for (const s of SCENARIOS) {
    ownerIncome.fixed[s] = rent - ownerOpex("fixed", s);
    ownerIncome.management[s] = revs[s] - ownerOpex("management", s) - mgmtFee[s];
    ownerIncome.mg[s] = mgPayout[s] - ownerOpex("mg", s);
  }

  // ---- Operator internals ----
  const operator = { fixed: {}, management: {}, mg: {} };
  for (const s of SCENARIOS) {
    const rev = revs[s];
    operator.fixed[s] = { revenue: rev, expenses: hrOpex("fixed", s), central: central[s], ownerPayout: rent, profit: rev - hrOpex("fixed", s) - central[s] - rent };
    operator.management[s] = { revenue: mgmtFee[s], expenses: 0, central: central[s], ownerPayout: ownerIncome.management[s], profit: mgmtFee[s] - central[s] };
    operator.mg[s] = { revenue: rev, expenses: hrOpex("mg", s), central: central[s], ownerPayout: mgPayout[s], profit: rev - hrOpex("mg", s) - central[s] - mgPayout[s] };
    for (const m of ["fixed", "management", "mg"]) {
      const o = operator[m][s];
      o.margin = rev ? (o.profit / rev) * 100 : 0;
      o.viability = viabilityStatus(o.margin, cfg);
    }
  }

  // ---- Break-even revenue (operator profit = 0), using cost structure fractions ----
  const parts = opexParts(expenses, R);
  const centralPct = N(cfg.central_support_pct);
  const breakEven = {};
  {
    const denomFixed = 1 - parts.varPct / 100 - centralPct / 100;
    breakEven.fixed = denomFixed > 0 ? (parts.fixedAmt + rent) / denomFixed : null;
    breakEven.mg = null;
    if (ms.payout_method === "higher_of") {
      const d1 = 1 - parts.varPct / 100 - centralPct / 100; // MG floor region
      const be1 = d1 > 0 ? (parts.fixedAmt + minGuarantee) / d1 : null;
      const d2 = 1 - parts.varPct / 100 - centralPct / 100 - sharePctEff / 100;
      const be2 = d2 > 0 ? parts.fixedAmt / d2 : null;
      const crossover = sharePctEff > 0 ? minGuarantee / (sharePctEff / 100) : Infinity;
      breakEven.mg = be1 !== null && be1 <= crossover ? be1 : be2;
    } else if (denomFixed - N(ms.upside_pct) / 100 !== 0) {
      breakEven.mg = breakEven.fixed !== null ? (parts.fixedAmt + minGuarantee) / (1 - parts.varPct / 100 - centralPct / 100) : null;
    }
    // Management: HR profit = fee - central; break-even where fee(rev) = central(rev)
    const feePct = N(mg.fee_pct);
    breakEven.management = feePct > centralPct ? 0 : null;
  }

  const maxSustainablePayout = revs.conservative - opex.conservative - central.conservative - targetProfitCons;

  // ---- Opportunity cost of fixed rent ----
  const oppCost = {
    mgmt_projected: ownerIncome.management.projected - ownerIncome.fixed.projected,
    mg_projected: ownerIncome.mg.projected - ownerIncome.fixed.projected,
    mgmt_high: ownerIncome.management.high - ownerIncome.fixed.high,
    mg_high: ownerIncome.mg.high - ownerIncome.fixed.high,
  };

  // ---- Warnings (internal) ----
  const warnings = [];
  const opexPctTotal = revs.projected ? (opex.projected / revs.projected) * 100 : 0;
  const reviewPct = N(cfg.viability?.review_pct ?? 10);
  for (const m of ["fixed", "management", "mg"]) {
    if (operator[m].projected.margin < minProfitPct) warnings.push(`${m === "fixed" ? "Fixed Rental" : m === "management" ? "Management Model" : "MG with Revenue Share"}: operator margin (${operator[m].projected.margin.toFixed(1)}%) is below the configured minimum (${minProfitPct}%) at projected revenue`);
  }
  if (rent > sustainableCeiling) warnings.push("Fixed rent is not sustainable under the conservative scenario");
  if (minGuarantee > maxSustainablePayout) warnings.push("Minimum guarantee exceeds sustainable owner payout");
  if (ownerIncome.management.conservative < 0) warnings.push("Management model owner return becomes negative under the conservative scenario");
  if (opexPctTotal > N(cfg.viability?.opex_warn_pct ?? 75)) warnings.push(`Operating-expense assumptions (${opexPctTotal.toFixed(1)}%) exceed the configured limit (${N(cfg.viability?.opex_warn_pct ?? 75)}%)`);
  if (operator.mg.projected.margin < reviewPct) warnings.push("Revenue share requires review — operator margin is below the review threshold");

  return {
    R, revs, opex, central, expenseRows, rent, pctRent, sustainableCeiling, targetProfitCons,
    mgmtFee, minGuarantee, mgShareAmt, mgPayout, ownerIncome, operator, oppCost,
    breakEven, maxSustainablePayout, warnings, opexPctTotal,
    rentPctEff, sharePctEff, stageBands: ss ? ss.bands : null, stageOpt: ss ? ss.opt : null,
  };
}

export function viabilityStatus(marginPct, cfg) {
  const healthy = N(cfg.viability?.healthy_pct ?? 15);
  const review = N(cfg.viability?.review_pct ?? 10);
  if (marginPct >= healthy) return "Healthy";
  if (marginPct >= review) return "Review Required";
  return "Not Sustainable";
}
